import { SxProps, Theme } from "@mui/material"
import { DataGridPro, DataGridProProps, GridColDef, GridRowEditStartParams } from "@mui/x-data-grid-pro"
import { Checkbox } from "@omnia/shared/react/checkbox"

interface GridProps<RowType> extends DataGridProProps<RowType> {
    columns: GridColDef[]
    rows: RowType[]
    onRowEditStart?: (params: GridRowEditStartParams<RowType>) => void
}

// export type Column<T = any> = GridColDef<T> & {
//   field: string;
//   headerName: string;
//   valueGetter?: (params: { row: T }) => any;
// };

export type Column<T> = { field: keyof T } & GridColDef<T>

export default function CustomDataGrid<RowType>({
    hideFooter = true,
    disableColumnMenu = true,
    rowSelection = true,
    density = "compact",
    rowHeight = 55,
    ...props
}: GridProps<RowType>) {
    const sx: SxProps<Theme> = {
        border: 0,
        fontSize: 13,
        fontFamily: '"Open Sans", "Helvetica Neue", Helvetica',
        color: "#555",
        "--unstable_DataGrid-radius": "0px",

        "& .MuiDataGrid-sortIcon": {
            opacity: 1,
            color: "#fff",
            fontSize: 16,
            fontWeight: 600,
        },
        //TODO fix stripes with expanded rows
        // "& .MuiDataGrid-row:nth-of-type(odd)": {
        //   backgroundColor: "#FAFAFA",
        // },
        "& .MuiDataGrid-cell": {
            border: "none",
            borderTop: "1px solid rgba(189, 193, 193, 0.2)",
        },
        "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft": {
            borderTop: "1px solid rgba(189, 193, 193, 0.2)",
        },
        "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedLeft.MuiDataGrid-cell--withRightBorder": {
            borderRight: "1px solid rgba(189, 193, 193, 0.2)",
        },
        "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedRight": {
            borderTop: "1px solid rgba(189, 193, 193, 0.2)",
        },
        "& .MuiDataGrid-cell.MuiDataGrid-cell--pinnedRight.MuiDataGrid-cell--withLeftBorder": {
            borderLeft: "1px solid rgba(189, 193, 193, 0.2)",
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#114768",
            color: "#fff",
            fontWeight: 600,
            borderBottom: "none",
            ".MuiSvgIcon-root": {
                color: "white",
            },
        },
        "& .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
            backgroundColor: "#114768",
        },
        "& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--header": {
            backgroundColor: "#114768",
            color: "#fff",
            fontWeight: 600,
            borderBottom: "none",
        },
        "& .MuiDataGrid-columnHeader .MuiSvgIcon-root.MuiDataGrid-iconSeparator": {
            color: "#7e96a5",
        },
        "& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--header.MuiDataGrid-scrollbarFiller--pinnedRight": {
            backgroundColor: "#114768",
        },
        "& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder": {
            borderRightColor: "#114768",
        },
        "& .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-columnHeader--withLeftBorder": {
            borderLeftColor: "#114768",
        },
        "& .MuiDataGrid-columnHeaderDraggableContainer": {},
        "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
        },
        "& .MuiDataGrid-row:hover .action-icon": {
            visibility: "visible",
        },
        "& .MuiDataGrid-row .action-icon": {
            visibility: "hidden",
        },
        "& .action-icon": {
            display: "inline-flex",
            alignItems: "center",
            gridGap: 8,
        },
        "& .Mui-selected": {
            fontWeight: 600, // This will make the font bold for selected rows
        },
        "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "#e4ebef",
        },
        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none",
        },
        // This is our custom checkbox, when used in the grid.
        "& .checkbox.icons": {
            margin: 0,
            padding: "5px",
        },
        // This is our custom checkbox, when shown in a column header (for the selection column)
        "& .MuiDataGrid-columnHeaderTitleContainer .checkbox.icons label input[type=checkbox] ~ i": {
            color: "#fff",
        },
    }

    return (
        <DataGridPro
            // These props use a value we provided a default for.
            hideFooter={hideFooter}
            disableColumnMenu={disableColumnMenu}
            rowSelection={rowSelection}
            density={density}
            rowHeight={rowHeight}
            // The props provided by the consumer.
            {...props}
            sx={{
                ...sx,
                ...props.sx,
            }}
            slots={{
                detailPanelExpandIcon: () => <i className="fas fa-angle-down"></i>,
                detailPanelCollapseIcon: () => <i className="fas fa-angle-up"></i>,
                baseCheckbox: Checkbox as any,
                ...props.slots,
            }}
        />
    )
}
